// Needed to enumerate English trigger names for translateTriggerText()
import en from "./locales/en.json";

// Helpers for translating DB reference data (symptoms, triggers, therapies).
// The DB value is always the canonical English name; these only affect display.

// DB values and translation keys sometimes differ only by apostrophe type
// (e.g. "writer's" vs "writer’s"), so try both.
const apostropheVariants = (value) => {
  const straight = value.replace(/[\u2018\u2019`\u00B4]/g, "'");
  const curly = straight.replace(/'/g, "\u2019");
  return [...new Set([value, straight, curly])];
};

// Returns the translation, or `fallback` if the key doesn't exist.
export const translateOr = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

// Translate a reference-data value, e.g.
//   translateDataValue(t, "onboarding.symptomListNew", "motor", "Allergies")
// Falls back to the original value if no translation exists.
export const translateDataValue = (t, prefix, category, value) => {
  if (typeof value !== "string") return value;
  for (const variant of apostropheVariants(value)) {
    const key = `${prefix}.${category}.${variant}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return value;
};

// TEMPORARY: the backend still expects "english"/"spanish" (e.g. email templates).
// Remove once the backend language-code migration is done.
export const toLegacyLanguage = (code) =>
  ({ en: "english", es: "spanish" }[code] || "english");

// Symptom categories used across onboarding + dashboard
export const SYMPTOM_CATEGORIES = [
  "motor",
  "sensory",
  "emotional",
  "behavioral",
  "cognitive",
  "immune",
  "metabolic",
];

// Translate a symptom name for display. Tries its own subcategory first, then
// every category (some callers don't know the category). Falls back to the original.
export const translateSymptom = (t, name, subcategory) => {
  if (!name) return name;
  const categories = [subcategory && subcategory.toLowerCase(), ...SYMPTOM_CATEGORIES].filter(Boolean);
  for (const category of categories) {
    const translated = translateDataValue(t, "onboarding.symptomListNew", category, name);
    if (translated !== name) return translated;
  }
  return name;
};

// Trigger categories (subcategory values used in the DB)
export const TRIGGER_CATEGORIES = [
  "wellness",
  "travel",
  "screen time",
  "environmental",
  "Dietary",
  "situational",
  "Other",
];

// Translate a single trigger name when its category isn't known.
export const translateTrigger = (t, name) => {
  if (!name) return name;
  for (const category of TRIGGER_CATEGORIES) {
    const translated = translateDataValue(t, "onboarding.triggerList", category, name);
    if (translated !== name) return translated;
  }
  return name;
};

// All English trigger names from en.json, longest first so e.g.
// "Seasonal Spring, Summer" is replaced before "Spring".
const TRIGGER_PREFIX = "onboarding.triggerList.";
let triggerIndexCache = null;
const getTriggerIndex = () => {
  if (!triggerIndexCache) {
    triggerIndexCache = Object.keys(en)
      .filter((key) => key.startsWith(TRIGGER_PREFIX))
      .map((key) => {
        const rest = key.slice(TRIGGER_PREFIX.length);
        const category = TRIGGER_CATEGORIES.find((c) => rest.startsWith(`${c}.`));
        return category ? { category, english: rest.slice(category.length + 1) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.english.length - a.english.length);
  }
  return triggerIndexCache;
};

// Translate free-form text that contains trigger names, e.g. a comma-joined
// description like "Car, Phone, Storm". Display only.
export const translateTriggerText = (t, text) => {
  if (!text) return text;
  let result = text;
  getTriggerIndex().forEach(({ category, english }) => {
    if (result.includes(english)) {
      const translated = translateDataValue(t, "onboarding.triggerList", category, english);
      result = result.split(english).join(translated);
    }
  });
  return result;
};

// Map app language codes to moment.js locale names
export const toMomentLocale = (code) =>
  ({ en: "en", es: "es", "zh-Hans": "zh-cn", id: "id" }[code] || "en");